import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Table, Button, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from "../../../api";

const ProcurementPipelineDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const indentId = searchParams.get("id") || searchParams.get("indentId");

  const [chain, setChain] = useState(location.state?.chain || null);
  const [loading, setLoading] = useState(!location.state?.chain);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchChain = useCallback(async () => {
    if (!indentId && !chain?._id) {
      setError("No Indent ID provided");
      setLoading(false);
      return;
    }

    const targetId = indentId || chain?._id;
    try {
      if (!chain) setLoading(true);
      else setRefreshing(true);
      const res = await api.get(`/pipeline-tracking/chain/${targetId}`);
      if (res.data?.success && res.data?.data) {
        setChain(res.data.data);
        setError("");
      } else {
        if (!chain) setError(res.data?.message || "Failed to load requisition details");
      }
    } catch (err) {
      console.error("Error fetching pipeline chain details:", err);
      if (!chain) setError(err.response?.data?.message || err.message || "Failed to load details");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [indentId, chain?._id]);

  useEffect(() => {
    fetchChain();
  }, [fetchChain]);

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return isNaN(date.getTime())
      ? "—"
      : date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const getStageBadge = (stage) => {
    const baseStyle = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      fontWeight: 700,
      borderRadius: "50rem",
      fontSize: "12px",
      padding: "4px 12px",
    };
    switch (stage) {
      case "INDENT_PENDING":
        return <span style={{ ...baseStyle, backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }}>Indent Pending</span>;
      case "INDENT_APPROVED":
        return <span style={{ ...baseStyle, backgroundColor: "#ede9fe", color: "#5b21b6", border: "1px solid #ddd6fe" }}>Indent Approved</span>;
      case "PO_ISSUED":
        return <span style={{ ...baseStyle, backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" }}>PO Issued</span>;
      case "DELIVERED":
        return <span style={{ ...baseStyle, backgroundColor: "#ffedd5", color: "#9a3412", border: "1px solid #fdba74" }}>Delivered</span>;
      case "IN_EXECUTION":
        return <span style={{ ...baseStyle, backgroundColor: "#e0f2fe", color: "#0369a1", border: "1px solid #7dd3fc" }}>In DPR</span>;
      case "COMPLETED":
        return <span style={{ ...baseStyle, backgroundColor: "#dcfce7", color: "#14532d", border: "1px solid #86efac" }}>Completed</span>;
      default:
        return <span style={{ ...baseStyle, backgroundColor: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}>{stage || "—"}</span>;
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center py-5" style={{ minHeight: "450px" }}>
        <Spinner animation="border" variant="primary" />
        <span className="mt-3 text-muted font-w600 fs-14">Loading Pipeline Flow...</span>
      </div>
    );
  }

  if (error || !chain) {
    return (
      <div className="container-fluid py-4">
        <Card className="border-0 shadow-sm text-center py-5">
          <Card.Body>
            <i className="fas fa-exclamation-triangle text-warning fa-3x mb-3"></i>
            <h4 className="font-w700 mb-2">Requisition Not Found</h4>
            <p className="text-muted mb-4 fs-14">{error || "Could not load pipeline details."}</p>
            <Button variant="primary" size="sm" onClick={() => navigate(-1)} className="font-w600 px-4">
              <i className="fas fa-arrow-left me-2"></i> Go Back to Pipeline
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  const totalRequested = chain.metrics?.totalRequestedQty || 0;
  const totalOrdered = chain.metrics?.totalOrderedQty || 0;
  const totalPoValue = chain.metrics?.totalPoValue || 0;

  // Calculate actual delivered from deliveries
  let totalDelivered = 0;
  (chain.deliveries || []).forEach((del) => {
    (del.items || []).forEach((itm) => {
      totalDelivered += Number(itm.quantityDelivered ?? itm.receivedQuantity ?? itm.challanQuantity ?? itm.quantity ?? 0);
    });
  });

  const totalExecuted = chain.metrics?.totalExecutedQty || 0;

  const poPercent = totalRequested > 0 ? Math.min(100, Math.round((totalOrdered / totalRequested) * 100)) : 0;
  const deliveryPercent = totalOrdered > 0 ? Math.min(100, Math.round((totalDelivered / totalOrdered) * 100)) : 0;
  const executionPercent = chain.metrics?.completionPercent || 0;

  // Item-Centric Mapping for Purchase Orders and Deliveries
  const indentItemsList = (chain.indent?.items && chain.indent.items.length > 0)
    ? chain.indent.items
    : (chain.pos?.length > 0 || chain.deliveries?.length > 0)
      ? [{
          itemName: chain.indent?.indentNumber ? `Requisition Materials (${chain.indent.indentNumber})` : "Requisition Materials",
          quantityRequested: totalRequested || totalOrdered || 1,
          unit: "Nos",
        }]
      : [];

  const isItemMatching = (indentItm, otherItm) => {
    if (!indentItm || !otherItm) return false;
    if (indentItemsList.length === 1) return true;

    if (indentItm._id && otherItm.indentItemId && String(indentItm._id) === String(otherItm.indentItemId)) {
      return true;
    }
    if (indentItm.itemId && otherItm.itemId && String(indentItm.itemId) === String(otherItm.itemId)) {
      return true;
    }

    const iName = (indentItm.itemName || indentItm.name || "").trim().toLowerCase();
    const oName = (otherItm.itemName || otherItm.name || "").trim().toLowerCase();
    if (!iName || !oName) return false;
    if (iName === oName) return true;

    const cleanI = iName.replace(/^\[[^\]]+\]\s*/i, "").trim();
    const cleanO = oName.replace(/^\[[^\]]+\]\s*/i, "").trim();
    if (cleanI && cleanO && cleanI === cleanO) return true;

    if (cleanI.length > 4 && cleanO.length > 4) {
      if (cleanI.includes(cleanO) || cleanO.includes(cleanI)) return true;
    }

    if (Array.isArray(indentItm.subItems) && indentItm.subItems.length > 0) {
      return indentItm.subItems.some((s) => {
        const sName = (s.name || "").trim().toLowerCase();
        if (!sName) return false;
        return sName === oName || oName.includes(sName) || sName.includes(oName);
      });
    }

    return false;
  };

  // Build PO Rows per Indent Item
  const poTableRows = indentItemsList.map((itm, idx) => {
    const raisedQty = Number(itm.quantityRequested || 0);
    const unit = itm.unit || "Nos";

    const associatedPOs = [];
    let itemPoQty = 0;
    let itemAmount = 0;

    (chain.pos || []).forEach((po) => {
      (po.items || []).forEach((pItm) => {
        if (isItemMatching(itm, pItm)) {
          const qty = Number(pItm.quantity || 0);
          const rate = Number(pItm.rate || 0);
          const amt = Number(pItm.amount || (qty * rate) || 0);
          itemPoQty += qty;
          itemAmount += amt;
          associatedPOs.push({
            poNumber: po.poNumber,
            poId: po._id,
            vendorName: po.vendorName || "Vendor",
            poStatus: po.status || "Draft",
            date: po.createdAt,
            quantity: qty,
            unit: pItm.unit || unit,
            rate: rate,
            amount: amt,
          });
        }
      });
    });

    if (associatedPOs.length === 0 && (chain.pos || []).length > 0 && indentItemsList.length === 1) {
      (chain.pos || []).forEach((po) => {
        const qty = Number(totalOrdered || 0);
        const amt = Number(po.totalAmount || 0);
        itemPoQty += qty;
        itemAmount += amt;
        associatedPOs.push({
          poNumber: po.poNumber,
          poId: po._id,
          vendorName: po.vendorName || "Vendor",
          poStatus: po.status || "Draft",
          date: po.createdAt,
          quantity: qty,
          unit: unit,
          rate: 0,
          amount: amt,
        });
      });
    }

    const balanceQty = Math.max(0, raisedQty - itemPoQty);
    const percent = raisedQty > 0 ? Math.round((itemPoQty / raisedQty) * 100) : (itemPoQty > 0 ? 100 : 0);

    let statusText = "Pending PO";
    let statusStyle = { backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" };

    if (itemPoQty >= raisedQty && raisedQty > 0) {
      statusText = `Full (${percent}%)`;
      statusStyle = { backgroundColor: "#dcfce7", color: "#14532d", border: "1px solid #86efac" };
    } else if (itemPoQty > 0) {
      statusText = `Partial (${percent}%)`;
      statusStyle = { backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" };
    }

    return {
      index: idx + 1,
      item: itm,
      raisedQty,
      unit,
      totalPoQty: itemPoQty,
      balanceQty,
      percent,
      statusText,
      statusStyle,
      associatedPOs,
      totalAmount: itemAmount,
    };
  });

  // Build Delivery Rows per Indent Item
  const deliveryTableRows = indentItemsList.map((itm, idx) => {
    const raisedQty = Number(itm.quantityRequested || 0);
    const unit = itm.unit || "Nos";
    const poRow = poTableRows[idx];
    const poQty = poRow ? poRow.totalPoQty : 0;

    const associatedDeliveries = [];
    let itemDeliveredQty = 0;

    (chain.deliveries || []).forEach((del) => {
      (del.items || []).forEach((dItm) => {
        if (isItemMatching(itm, dItm)) {
          const qty = Number(dItm.quantityDelivered ?? dItm.receivedQuantity ?? dItm.challanQuantity ?? dItm.quantity ?? 0);
          itemDeliveredQty += qty;
          associatedDeliveries.push({
            deliveryNumber: del.deliveryNumber,
            challanNumber: del.challanNumber,
            vendorName: del.vendorName || "Vendor",
            status: del.status || "Delivered",
            date: del.createdAt,
            quantity: qty,
            unit: dItm.unit || unit,
          });
        }
      });
    });

    if (associatedDeliveries.length === 0 && (chain.deliveries || []).length > 0 && indentItemsList.length === 1) {
      (chain.deliveries || []).forEach((del) => {
        const qty = Number(totalDelivered || 0);
        itemDeliveredQty += qty;
        associatedDeliveries.push({
          deliveryNumber: del.deliveryNumber,
          challanNumber: del.challanNumber,
          vendorName: del.vendorName || "Vendor",
          status: del.status || "Delivered",
          date: del.createdAt,
          quantity: qty,
          unit: unit,
        });
      });
    }

    const pendingDel = Math.max(0, poQty > 0 ? (poQty - itemDeliveredQty) : 0);
    const delPercent = poQty > 0 ? Math.round((itemDeliveredQty / poQty) * 100) : 0;

    let statusText = "Pending Delivery";
    let statusStyle = { backgroundColor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" };

    if (itemDeliveredQty >= poQty && poQty > 0) {
      statusText = "Fully Delivered";
      statusStyle = { backgroundColor: "#dcfce7", color: "#14532d", border: "1px solid #86efac" };
    } else if (itemDeliveredQty > 0) {
      statusText = `Partial (${delPercent}%)`;
      statusStyle = { backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" };
    } else if (poQty === 0) {
      statusText = "Awaiting PO";
      statusStyle = { backgroundColor: "#f1f5f9", color: "#64748b", border: "1px solid #cbd5e1" };
    }

    return {
      index: idx + 1,
      item: itm,
      raisedQty,
      unit,
      poQty,
      deliveredQty: itemDeliveredQty,
      pendingDel,
      delPercent,
      statusText,
      statusStyle,
      associatedDeliveries,
    };
  });

  const sumPoRaisedQty = poTableRows.reduce((a, b) => a + b.raisedQty, 0);
  const sumPoTotalQty = poTableRows.reduce((a, b) => a + b.totalPoQty, 0);
  const sumPoBalanceQty = poTableRows.reduce((a, b) => a + b.balanceQty, 0);
  const sumPoTotalAmount = poTableRows.reduce((a, b) => a + b.totalAmount, 0);

  const sumDelRaisedQty = deliveryTableRows.reduce((a, b) => a + b.raisedQty, 0);
  const sumDelPoQty = deliveryTableRows.reduce((a, b) => a + b.poQty, 0);
  const sumDeliveredQty = deliveryTableRows.reduce((a, b) => a + b.deliveredQty, 0);
  const sumPendingDelQty = deliveryTableRows.reduce((a, b) => a + b.pendingDel, 0);

  // Visual Pipeline Stages Definition
  const pipelineSteps = [
    {
      step: 1,
      title: "1. Indent Raised",
      subtitle: `${totalRequested} Nos Requested`,
      statusText: chain.indent?.status || "Pending",
      statusClass: "success",
      icon: "fa-clipboard-list",
      isDone: true,
      isActive: false,
      detail: `Created on ${formatDate(chain.indent?.createdAt)}`,
    },
    {
      step: 2,
      title: "2. PO Issued",
      subtitle: `${totalOrdered} / ${totalRequested} Nos Ordered`,
      statusText: `${poPercent}% Ordered`,
      statusClass: poPercent === 100 ? "success" : "primary",
      icon: "fa-file-invoice-dollar",
      isDone: totalOrdered >= totalRequested && totalRequested > 0,
      isActive: totalOrdered > 0 && totalOrdered < totalRequested,
      detail: `${chain.pos?.length || 0} POs • ₹${Math.round(totalPoValue).toLocaleString("en-IN")}`,
    },
    {
      step: 3,
      title: "3. Site Delivery",
      subtitle: `${totalDelivered} / ${totalOrdered || totalRequested} Nos Received`,
      statusText: totalDelivered > 0 ? `${deliveryPercent}% Received` : "Pending Delivery",
      statusClass: totalDelivered > 0 ? (deliveryPercent === 100 ? "success" : "info") : "warning",
      icon: "fa-truck-moving",
      isDone: totalDelivered >= (totalOrdered || totalRequested) && totalOrdered > 0,
      isActive: totalOrdered > 0 && totalDelivered < totalOrdered,
      detail: `${chain.deliveries?.length || 0} Challans Received`,
    },
    {
      step: 4,
      title: "4. Site Installation",
      subtitle: `${totalExecuted} / ${totalRequested} Nos Installed`,
      statusText: totalExecuted > 0 ? `${executionPercent}% Installed` : "Awaiting Site Work",
      statusClass: totalExecuted > 0 ? (executionPercent === 100 ? "success" : "info") : "secondary",
      icon: "fa-hard-hat",
      isDone: totalExecuted >= totalRequested && totalRequested > 0,
      isActive: totalDelivered > 0 && totalExecuted < totalRequested,
      detail: `${chain.dprExecution?.totalDPRsCount || 0} DPR Daily Logs`,
    },
  ];

  return (
    <div className="container-fluid px-3 py-2">
      {/* 1. TOP HEADER & BREADCRUMB */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="d-inline-flex align-items-center justify-content-center p-0 rounded-circle shadow-xs"
            style={{ width: "36px", height: "36px", minWidth: "36px" }}
            onClick={() => navigate(-1)}
            title="Go Back"
          >
            <i className="fas fa-arrow-left" style={{ fontSize: "14px" }}></i>
          </Button>

          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h4 className="font-w700 text-dark mb-0">Requisition Lifecycle</h4>
              <span
                className="font-w700 fs-13 px-2.5 py-1 rounded"
                style={{ backgroundColor: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}
              >
                {chain.indent?.indentNumber}
              </span>
              {getStageBadge(chain.currentStage)}
            </div>
            <span className="text-muted fs-12">
              <i className="fas fa-building me-1 text-primary"></i> Project: <strong className="text-dark">{chain.project?.projectName || "—"}</strong>
              <span className="mx-2">•</span>
              <i className="fas fa-file-contract me-1 text-secondary"></i> Work Order: <strong className="text-dark">{chain.workOrder?.label ? `${chain.workOrder.label} (${chain.workOrder.woNo || "—"})` : (chain.workOrder?.woNo || "—")}</strong>
              <span className="mx-2">•</span>
              <i className="far fa-calendar-alt me-1 text-info"></i> Date: <strong>{formatDate(chain.indent?.createdAt)}</strong>
            </span>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            className="shadow-xs font-w600 d-inline-flex align-items-center gap-2 px-3"
            style={{
              height: "36px",
              backgroundColor: "#ffffff",
              borderColor: "#cbd5e1",
              color: "#1e293b",
            }}
            onClick={fetchChain}
            disabled={refreshing}
          >
            <i className={`fas fa-sync-alt ${refreshing ? "fa-spin" : ""}`} style={{ color: "#2563eb" }}></i>
            <span style={{ color: "#1e293b", fontWeight: 600 }}>Refresh</span>
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="font-w600 d-inline-flex align-items-center gap-1 shadow-xs px-3"
            style={{ height: "36px" }}
            onClick={() => navigate("/pipeline-dashboard")}
          >
            <i className="fas fa-columns me-1"></i> All Pipeline Items
          </Button>
        </div>
      </div>

      {/* 2. THE VISUAL LIFECYCLE FLOW (CLEAR STEP-BY-STEP PROGRESSION) */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <style>{`
            [data-theme-version="dark"] .supply-chain-overall-badge {
              background-color: rgba(255, 255, 255, 0.06) !important;
              color: #e2e8f0 !important;
              border-color: rgba(255, 255, 255, 0.12) !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-done {
              background: linear-gradient(135deg, rgba(16, 185, 129, 0.16) 0%, rgba(6, 78, 59, 0.22) 100%) !important;
              border-color: rgba(52, 211, 153, 0.45) !important;
              box-shadow: 0 4px 14px rgba(16, 185, 129, 0.12) !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-done .step-circle {
              background-color: #10b981 !important;
              color: #ffffff !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-done .step-badge {
              background-color: rgba(16, 185, 129, 0.22) !important;
              color: #34d399 !important;
              border-color: rgba(52, 211, 153, 0.45) !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-done .step-title {
              color: #ffffff !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-done .step-subtitle {
              color: #cbd5e1 !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-done .step-detail {
              color: #6ee7b7 !important;
              border-top-color: rgba(255, 255, 255, 0.08) !important;
            }

            [data-theme-version="dark"] .pipeline-step-card.is-active {
              background: linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(30, 58, 138, 0.12) 100%) !important;
              border-color: rgba(96, 165, 250, 0.5) !important;
              box-shadow: 0 4px 14px rgba(59, 130, 246, 0.12) !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-active .step-circle {
              background-color: #3b82f6 !important;
              color: #ffffff !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-active .step-badge {
              background-color: rgba(59, 130, 246, 0.25) !important;
              color: #93c5fd !important;
              border-color: rgba(96, 165, 250, 0.45) !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-active .step-title {
              color: #ffffff !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-active .step-subtitle {
              color: #cbd5e1 !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-active .step-detail {
              color: #93c5fd !important;
              border-top-color: rgba(255, 255, 255, 0.08) !important;
            }

            [data-theme-version="dark"] .pipeline-step-card.is-pending {
              background: rgba(255, 255, 255, 0.03) !important;
              border-color: rgba(255, 255, 255, 0.09) !important;
              box-shadow: none !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-pending .step-circle {
              background-color: rgba(148, 163, 184, 0.25) !important;
              color: #94a3b8 !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-pending .step-badge {
              background-color: rgba(255, 255, 255, 0.06) !important;
              color: #94a3b8 !important;
              border-color: rgba(255, 255, 255, 0.12) !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-pending .step-title {
              color: #e2e8f0 !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-pending .step-subtitle {
              color: #64748b !important;
            }
            [data-theme-version="dark"] .pipeline-step-card.is-pending .step-detail {
              color: #94a3b8 !important;
              border-top-color: rgba(255, 255, 255, 0.07) !important;
            }
            [data-theme-version="dark"] .velocity-progress-container {
              background-color: rgba(255, 255, 255, 0.1) !important;
            }
          `}</style>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="fs-12 font-w700 text-uppercase text-muted">
              <i className="fas fa-route text-primary me-2"></i> Supply Chain Progress Track
            </span>
            <span
              className="supply-chain-overall-badge"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                fontWeight: 700,
                borderRadius: "50rem",
                fontSize: "12px",
                padding: "4px 14px",
                backgroundColor: "#f8fafc",
                color: "#1e293b",
                border: "1px solid #cbd5e1",
              }}
            >
              Overall Status: <strong style={{ color: "#1d4ed8", marginLeft: "4px" }}>{chain.stageProgress || 0}% Complete</strong>
            </span>
          </div>

          {/* Stepper Pipeline Cards */}
          <Row className="g-2 align-items-stretch">
            {pipelineSteps.map((step, idx) => (
              <Col lg={3} sm={6} key={idx}>
                <div
                  className={`p-3 rounded h-100 position-relative transition-all pipeline-step-card ${
                    step.isDone ? "is-done" : step.isActive ? "is-active" : "is-pending"
                  }`}
                  style={{
                    backgroundColor: step.isDone ? "#f0fdf4" : step.isActive ? "#eff6ff" : "#f8fafc",
                    border: `1.5px solid ${step.isDone ? "#86efac" : step.isActive ? "#93c5fd" : "#e2e8f0"}`,
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white font-w700 step-circle"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: step.isDone ? "#16a34a" : step.isActive ? "#2563eb" : "#94a3b8",
                        fontSize: "13px",
                        flexShrink: 0,
                      }}
                    >
                      {step.isDone ? <i className="fas fa-check"></i> : step.step}
                    </div>

                    <span
                      className="step-badge"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        fontWeight: 700,
                        borderRadius: "50rem",
                        fontSize: "11px",
                        padding: "4px 10px",
                        backgroundColor: step.isDone ? "#dcfce7" : step.isActive ? "#dbeafe" : "#f1f5f9",
                        color: step.isDone ? "#14532d" : step.isActive ? "#1e40af" : "#475569",
                        border: `1px solid ${step.isDone ? "#86efac" : step.isActive ? "#93c5fd" : "#cbd5e1"}`,
                      }}
                    >
                      {step.statusText}
                    </span>
                  </div>

                  <div className="font-w700 text-dark fs-14 mb-1 step-title">{step.title}</div>
                  <div className="text-muted fs-12 font-w600 mb-2 step-subtitle">{step.subtitle}</div>

                  <div className="fs-11 text-secondary pt-2 border-top border-light step-detail">
                    {step.detail}
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* Comparative Progress Bar */}
          <div className="mt-3 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center fs-12 mb-1 text-muted">
              <span>Fulfillment Velocity</span>
              <span>
                Ordered: <strong style={{ color: "#1d4ed8" }}>{poPercent}%</strong> | Delivered: <strong style={{ color: "#c2410c" }}>{deliveryPercent}%</strong> | Installed: <strong style={{ color: "#15803d" }}>{executionPercent}%</strong>
              </span>
            </div>
            <div className="d-flex rounded overflow-hidden velocity-progress-container" style={{ height: "8px", backgroundColor: "#e2e8f0" }}>
              <div style={{ width: `${poPercent}%`, backgroundColor: "#2563eb" }} title="Ordered" />
              <div style={{ width: `${deliveryPercent}%`, backgroundColor: "#ea580c" }} title="Delivered" />
              <div style={{ width: `${executionPercent}%`, backgroundColor: "#16a34a" }} title="Installed" />
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* 3. STEP-BY-STEP DETAIL TABLES (ALL CONTENT IN CLEAN TABLE FORMAT) */}

      {/* TABLE 1: WHAT WAS REQUESTED (THE INDENT) */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <span
              className="rounded-circle d-flex align-items-center justify-content-center text-white"
              style={{ width: "26px", height: "26px", backgroundColor: "#0d6efd", fontSize: "12px", fontWeight: "700" }}
            >
              1
            </span>
            <h5 className="font-w700 text-dark mb-0">Requisition Items (What the Site Requested)</h5>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              fontWeight: 700,
              borderRadius: "50rem",
              fontSize: "13px",
              padding: "5px 14px",
              backgroundColor: "#eff6ff",
              color: "#1d4ed8",
              border: "1px solid #bfdbfe",
            }}
          >
            Total {totalRequested} Units Requested
          </span>
        </Card.Header>

        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="table-responsive-md align-middle mb-0 fs-13">
              <thead className="bg-light text-muted fs-11 font-w600 text-uppercase">
                <tr>
                  <th style={{ width: "45px" }} className="text-center">#</th>
                  <th>Item Name & Sub-items</th>
                  <th className="text-center" style={{ width: "160px" }}>Requested Qty</th>
                  <th style={{ width: "120px" }}>Unit</th>
                  <th className="text-center" style={{ width: "140px" }}>Billing Type</th>
                </tr>
              </thead>
              <tbody>
                {(chain.indent?.items || []).map((itm, i) => (
                  <tr key={i}>
                    <td className="text-center text-muted fs-12">{i + 1}</td>
                    <td>
                      <div className="font-w700 text-dark fs-14">{itm.itemName}</div>
                      {itm.subItems && itm.subItems.length > 0 && (
                        <div className="mt-1 ps-3 border-start border-2 border-primary" style={{ fontSize: "0.8rem" }}>
                          <span className="text-muted font-w600">Sub-items: </span>
                          {itm.subItems.map((s, sIdx) => (
                            <span
                              key={sIdx}
                              className="font-w600 px-2 py-0.5 rounded me-1 my-0.5 d-inline-block"
                              style={{ backgroundColor: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}
                            >
                              {s.name} ({s.quantity} {s.unit || itm.unit || ""})
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="text-center font-w700 fs-15" style={{ color: "#1d4ed8" }}>{itm.quantityRequested}</td>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                          fontSize: "12px",
                          padding: "3px 8px",
                          borderRadius: "4px",
                          backgroundColor: "#f1f5f9",
                          color: "#334155",
                          border: "1px solid #cbd5e1",
                        }}
                      >
                        {itm.unit || "Nos"}
                      </span>
                    </td>
                    <td className="text-center">
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          fontWeight: 700,
                          borderRadius: "50rem",
                          fontSize: "12px",
                          padding: "4px 10px",
                          ...(itm.isNonBillable
                            ? { backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }
                            : { backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" }),
                        }}
                      >
                        {itm.isNonBillable ? "Non-Billable" : "Standard BOQ"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* TABLE 2: WHAT WAS ORDERED (PURCHASE ORDERS TABLE) */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white border-bottom py-2.5 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <span
              className="rounded-circle d-flex align-items-center justify-content-center text-white"
              style={{ width: "24px", height: "24px", backgroundColor: "#0891b2", fontSize: "11px", fontWeight: "700" }}
            >
              2
            </span>
            <h5 className="font-w700 text-dark mb-0 fs-15">Purchase Orders Issued (Item-wise PO Tracking)</h5>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                fontWeight: 700,
                borderRadius: "50rem",
                fontSize: "11.5px",
                padding: "3px 10px",
                backgroundColor: "#dcfce7",
                color: "#14532d",
                border: "1px solid #86efac",
              }}
            >
              Total PO: ₹{Math.round(totalPoValue).toLocaleString("en-IN")}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                fontWeight: 700,
                borderRadius: "50rem",
                fontSize: "11.5px",
                padding: "3px 10px",
                backgroundColor: "#dbeafe",
                color: "#1e40af",
                border: "1px solid #93c5fd",
              }}
            >
              {totalOrdered} / {totalRequested} Ordered ({poPercent}%)
            </span>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <div className="w-100">
            <Table hover className="align-middle mb-0 fs-12 w-100" style={{ tableLayout: "auto" }}>
              <thead className="bg-light text-muted fs-11 font-w600 text-uppercase">
                <tr>
                  <th style={{ width: "4%" }} className="text-center py-2 px-1">#</th>
                  <th style={{ width: "24%" }} className="py-2 px-2">Item Name & Sub-Items</th>
                  <th style={{ width: "9%" }} className="text-center py-2 px-1">Raised Qty</th>
                  <th style={{ width: "9%" }} className="text-center py-2 px-1">Total PO Qty</th>
                  <th style={{ width: "9%" }} className="text-center py-2 px-1">Balance Qty</th>
                  <th style={{ width: "10%" }} className="text-center py-2 px-1">Status</th>
                  <th style={{ width: "24%" }} className="py-2 px-2">Associated POs Breakdown</th>
                  <th style={{ width: "11%" }} className="text-end py-2 px-2">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {poTableRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-muted bg-light">
                      <div className="py-2">
                        <i className="fas fa-shopping-cart fa-2x mb-2 text-secondary opacity-50 d-block"></i>
                        <span className="font-w600 text-dark fs-13 d-block">No Purchase Order Created Yet</span>
                        <span className="fs-11 text-muted">Purchase orders created against this requisition will automatically appear here.</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  poTableRows.map((row) => (
                    <tr key={row.index}>
                      <td className="text-center text-muted fs-11 py-2 px-1">{row.index}</td>
                      <td className="py-2 px-2">
                        <div className="font-w700 text-dark fs-12 line-clamp-2" title={row.item.itemName}>
                          {row.item.itemName}
                        </div>
                        {row.item.subItems && row.item.subItems.length > 0 && (
                          <div className="mt-1 ps-2 border-start border-2 border-primary" style={{ fontSize: "10.5px" }}>
                            <span className="text-muted font-w600">Sub-items: </span>
                            {row.item.subItems.map((s, sIdx) => (
                              <span
                                key={sIdx}
                                className="font-w600 px-1.5 py-0.5 rounded me-1 my-0.5 d-inline-block"
                                style={{ backgroundColor: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}
                              >
                                {s.name} ({s.quantity} {s.unit || row.unit})
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="text-center py-2 px-1 font-w600 text-muted">
                        {row.raisedQty} {row.unit}
                      </td>
                      <td className="text-center py-2 px-1 font-w700" style={{ color: "#1d4ed8" }}>
                        {row.totalPoQty} {row.unit}
                      </td>
                      <td className="text-center py-2 px-1 font-w600" style={{ color: row.balanceQty > 0 ? "#b45309" : "#16a34a" }}>
                        {row.balanceQty} {row.unit}
                      </td>
                      <td className="text-center py-2 px-1">
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 1.1,
                            fontWeight: 700,
                            borderRadius: "50rem",
                            fontSize: "10.5px",
                            padding: "2.5px 8px",
                            ...row.statusStyle,
                          }}
                        >
                          {row.statusText}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        {row.associatedPOs.length === 0 ? (
                          <span className="text-muted fst-italic fs-11">No PO created yet</span>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            {row.associatedPOs.map((pe, pIdx) => (
                              <div
                                key={pIdx}
                                className="p-1 px-1.5 rounded border bg-light bg-opacity-50 d-flex flex-wrap align-items-center justify-content-between gap-1"
                                style={{ fontSize: "11px" }}
                              >
                                <div className="text-truncate" style={{ maxWidth: "160px" }}>
                                  <strong className="text-primary fs-11">{pe.poNumber}</strong>
                                  <span className="text-muted ms-1 fs-10">({pe.vendorName})</span>
                                  <span
                                    className="badge ms-1"
                                    style={{
                                      fontSize: "9px",
                                      padding: "1.5px 4px",
                                      ...(/draft/i.test(pe.poStatus)
                                        ? { backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }
                                        : /approved/i.test(pe.poStatus)
                                        ? { backgroundColor: "#dcfce7", color: "#14532d", border: "1px solid #86efac" }
                                        : { backgroundColor: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }),
                                    }}
                                  >
                                    {pe.poStatus}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-1.5">
                                  <span className="badge bg-primary bg-opacity-10 text-primary font-w600" style={{ fontSize: "10px", padding: "2px 5px" }}>
                                    {pe.quantity} {pe.unit}
                                  </span>
                                  <strong className="text-success fs-11">
                                    ₹{Math.round(pe.amount).toLocaleString("en-IN")}
                                  </strong>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="text-end py-2 px-2">
                        <strong className="fs-12" style={{ color: "#15803d" }}>
                          ₹{Math.round(row.totalAmount).toLocaleString("en-IN")}
                        </strong>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {poTableRows.length > 0 && (
                <tfoot className="bg-light font-w700 text-dark fs-11 border-top">
                  <tr>
                    <td colSpan="2" className="text-end py-1.5 px-2 text-muted">Total:</td>
                    <td className="text-center py-1.5 px-1 font-w700">{sumPoRaisedQty}</td>
                    <td className="text-center py-1.5 px-1 font-w700 text-primary">{sumPoTotalQty}</td>
                    <td className="text-center py-1.5 px-1 font-w700" style={{ color: sumPoBalanceQty > 0 ? "#b45309" : "#16a34a" }}>
                      {sumPoBalanceQty}
                    </td>
                    <td colSpan="2" className="text-end py-1.5 px-2 text-muted">Total PO Value:</td>
                    <td className="text-end py-1.5 px-2 font-w700 fs-12 text-success">
                      ₹{Math.round(sumPoTotalAmount).toLocaleString("en-IN")}
                    </td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* TABLE 3: SITE DELIVERIES (DELIVERY CHALLANS / MIR TABLE) */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white border-bottom py-2.5 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <span
              className="rounded-circle d-flex align-items-center justify-content-center text-white"
              style={{ width: "24px", height: "24px", backgroundColor: "#f59e0b", fontSize: "11px", fontWeight: "700" }}
            >
              3
            </span>
            <h5 className="font-w700 text-dark mb-0 fs-15">Site Deliveries (Item-wise Delivery Tracking)</h5>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              fontWeight: 700,
              borderRadius: "50rem",
              fontSize: "11.5px",
              padding: "3px 10px",
              backgroundColor: "#ffedd5",
              color: "#9a3412",
              border: "1px solid #fdba74",
            }}
          >
            Delivered: {totalDelivered} / {totalOrdered || totalRequested} Nos ({deliveryPercent}%)
          </span>
        </Card.Header>

        <Card.Body className="p-0">
          <div className="w-100">
            <Table hover className="align-middle mb-0 fs-12 w-100" style={{ tableLayout: "auto" }}>
              <thead className="bg-light text-muted fs-11 font-w600 text-uppercase">
                <tr>
                  <th style={{ width: "4%" }} className="text-center py-2 px-1">#</th>
                  <th style={{ width: "24%" }} className="py-2 px-2">Item Name & Sub-Items</th>
                  <th style={{ width: "9%" }} className="text-center py-2 px-1">Raised Qty</th>
                  <th style={{ width: "9%" }} className="text-center py-2 px-1">PO Qty</th>
                  <th style={{ width: "9%" }} className="text-center py-2 px-1">Delivered Qty</th>
                  <th style={{ width: "9%" }} className="text-center py-2 px-1">Pending Del.</th>
                  <th style={{ width: "11%" }} className="text-center py-2 px-1">Status</th>
                  <th style={{ width: "25%" }} className="py-2 px-2">Associated Challans / Deliveries</th>
                </tr>
              </thead>
              <tbody>
                {deliveryTableRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4 bg-light text-muted">
                      <div className="py-2">
                        <i className="fas fa-truck-loading text-warning fa-2x mb-2 d-block opacity-75"></i>
                        <span className="font-w700 text-dark fs-13 d-block mb-1">
                          Awaiting First Dispatch from Vendor
                        </span>
                        <span className="fs-11 text-muted">
                          Once materials arrive at site and the site engineer logs the Delivery Challan (MIR), records will appear here.
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  deliveryTableRows.map((row) => (
                    <tr key={row.index}>
                      <td className="text-center text-muted fs-11 py-2 px-1">{row.index}</td>
                      <td className="py-2 px-2">
                        <div className="font-w700 text-dark fs-12 line-clamp-2" title={row.item.itemName}>
                          {row.item.itemName}
                        </div>
                        {row.item.subItems && row.item.subItems.length > 0 && (
                          <div className="mt-1 ps-2 border-start border-2 border-primary" style={{ fontSize: "10.5px" }}>
                            <span className="text-muted font-w600">Sub-items: </span>
                            {row.item.subItems.map((s, sIdx) => (
                              <span
                                key={sIdx}
                                className="font-w600 px-1.5 py-0.5 rounded me-1 my-0.5 d-inline-block"
                                style={{ backgroundColor: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}
                              >
                                {s.name} ({s.quantity} {s.unit || row.unit})
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="text-center py-2 px-1 font-w600 text-muted">
                        {row.raisedQty} {row.unit}
                      </td>
                      <td className="text-center py-2 px-1 font-w700 text-primary">
                        {row.poQty} {row.unit}
                      </td>
                      <td className="text-center py-2 px-1 font-w700 text-success">
                        {row.deliveredQty} {row.unit}
                      </td>
                      <td className="text-center py-2 px-1 font-w600" style={{ color: row.pendingDel > 0 ? "#c2410c" : "#16a34a" }}>
                        {row.pendingDel} {row.unit}
                      </td>
                      <td className="text-center py-2 px-1">
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: 1.1,
                            fontWeight: 700,
                            borderRadius: "50rem",
                            fontSize: "10.5px",
                            padding: "2.5px 8px",
                            ...row.statusStyle,
                          }}
                        >
                          {row.statusText}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        {row.associatedDeliveries.length === 0 ? (
                          <span className="text-muted fst-italic fs-11">No delivery received yet</span>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            {row.associatedDeliveries.map((de, dIdx) => (
                              <div
                                key={dIdx}
                                className="p-1 px-1.5 rounded border bg-light bg-opacity-50 d-flex flex-wrap align-items-center justify-content-between gap-1"
                                style={{ fontSize: "11px" }}
                              >
                                <div className="text-truncate" style={{ maxWidth: "160px" }}>
                                  <strong className="text-dark fs-11">{de.deliveryNumber}</strong>
                                  {de.challanNumber && de.challanNumber !== "N/A" && (
                                    <span className="text-muted ms-1 fs-10">(Challan: {de.challanNumber})</span>
                                  )}
                                  <span className="text-muted ms-1 fs-10">by {de.vendorName}</span>
                                  <span
                                    className="badge ms-1"
                                    style={{
                                      fontSize: "9px",
                                      padding: "1.5px 4px",
                                      backgroundColor: "#dcfce7",
                                      color: "#14532d",
                                      border: "1px solid #86efac",
                                    }}
                                  >
                                    {de.status}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-1.5">
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      fontWeight: 700,
                                      fontSize: "10px",
                                      padding: "1.5px 5px",
                                      borderRadius: "3px",
                                      backgroundColor: "#dcfce7",
                                      color: "#14532d",
                                      border: "1px solid #86efac",
                                    }}
                                  >
                                    {de.quantity} {de.unit}
                                  </span>
                                  <span className="text-muted fs-10">
                                    {formatDate(de.date)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {deliveryTableRows.length > 0 && (
                <tfoot className="bg-light font-w700 text-dark fs-11 border-top">
                  <tr>
                    <td colSpan="2" className="text-end py-1.5 px-2 text-muted">Total:</td>
                    <td className="text-center py-1.5 px-1 font-w700">{sumDelRaisedQty}</td>
                    <td className="text-center py-1.5 px-1 font-w700 text-primary">{sumDelPoQty}</td>
                    <td className="text-center py-1.5 px-1 font-w700 text-success">{sumDeliveredQty}</td>
                    <td className="text-center py-1.5 px-1 font-w700" style={{ color: sumPendingDelQty > 0 ? "#c2410c" : "#16a34a" }}>
                      {sumPendingDelQty}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* TABLE 4: SITE INSTALLATION PROGRESS (DAILY PROGRESS REPORTS - DPR) */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <span
              className="rounded-circle d-flex align-items-center justify-content-center text-white"
              style={{ width: "26px", height: "26px", backgroundColor: "#10b981", fontSize: "12px", fontWeight: "700" }}
            >
              4
            </span>
            <h5 className="font-w700 text-dark mb-0">Site Installation Progress (Daily Progress Reports - DPR)</h5>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              fontWeight: 700,
              borderRadius: "50rem",
              fontSize: "13px",
              padding: "5px 14px",
              backgroundColor: "#dcfce7",
              color: "#14532d",
              border: "1px solid #86efac",
            }}
          >
            Installed: {totalExecuted} / {totalRequested} Nos ({executionPercent}%)
          </span>
        </Card.Header>

        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="table-responsive-md align-middle mb-0 fs-13">
              <thead className="bg-light text-muted fs-11 font-w600 text-uppercase">
                <tr>
                  <th style={{ width: "45px" }} className="text-center">#</th>
                  <th style={{ minWidth: "220px" }}>Execution Milestone</th>
                  <th>Current Progress Details</th>
                  <th style={{ minWidth: "180px" }}>Target / Scope</th>
                  <th className="text-center" style={{ width: "150px" }}>Completion Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-center text-muted fs-12">1</td>
                  <td className="font-w700 text-dark">
                    <i className="fas fa-tools text-primary me-2"></i> Physical Installation at Site
                  </td>
                  <td>
                    <strong className="fs-14" style={{ color: "#1d4ed8" }}>{totalExecuted} Nos Installed</strong>
                    <div className="text-muted fs-12 mt-0.5">
                      {totalDelivered === 0 ? "Awaiting material delivery before site installation commences" : "Ongoing site installation work"}
                    </div>
                  </td>
                  <td className="text-muted">
                    <strong className="text-dark">{totalRequested} Nos</strong> Total Required Scope
                  </td>
                  <td className="text-center">
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        fontWeight: 700,
                        borderRadius: "50rem",
                        fontSize: "12px",
                        padding: "4px 12px",
                        backgroundColor: executionPercent === 100 ? "#dcfce7" : executionPercent > 0 ? "#dbeafe" : "#f1f5f9",
                        color: executionPercent === 100 ? "#14532d" : executionPercent > 0 ? "#1e40af" : "#475569",
                        border: `1px solid ${executionPercent === 100 ? "#86efac" : executionPercent > 0 ? "#bfdbfe" : "#cbd5e1"}`
                      }}
                    >
                      {executionPercent}% Installed
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="text-center text-muted fs-12">2</td>
                  <td className="font-w700 text-dark">
                    <i className="fas fa-book-open text-info me-2"></i> Daily Progress Reports (DPR Logs)
                  </td>
                  <td>
                    <strong className="text-dark fs-14">{chain.dprExecution?.totalDPRsCount || 0} Reports Recorded</strong>
                    <div className="text-muted fs-12 mt-0.5">
                      Latest Activity Date: <strong>{formatDate(chain.dprExecution?.latestDprDate)}</strong>
                    </div>
                  </td>
                  <td className="text-muted">
                    Work Order: <strong className="text-dark">{chain.workOrder?.label ? `${chain.workOrder.label} (${chain.workOrder.woNo || "—"})` : (chain.workOrder?.woNo || "—")}</strong>
                  </td>
                  <td className="text-center">
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        fontWeight: 700,
                        borderRadius: "50rem",
                        fontSize: "12px",
                        padding: "4px 12px",
                        backgroundColor: "#dcfce7",
                        color: "#14532d",
                        border: "1px solid #86efac",
                      }}
                    >
                      Active Daily Logs
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="text-center text-muted fs-12">3</td>
                  <td className="font-w700 text-dark">
                    <i className="fas fa-map-marker-alt text-danger me-2"></i> Site Allocation & Project
                  </td>
                  <td>
                    <strong className="text-dark fs-14">{chain.project?.projectName || "—"}</strong>
                    <div className="text-muted fs-12 mt-0.5">Assigned execution project</div>
                  </td>
                  <td className="text-muted">
                    Work Order: <strong className="text-dark">{chain.workOrder?.label ? `${chain.workOrder.label} (${chain.workOrder.woNo || "—"})` : (chain.workOrder?.woNo || "—")}</strong>
                  </td>
                  <td className="text-center">
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        fontWeight: 700,
                        borderRadius: "50rem",
                        fontSize: "12px",
                        padding: "4px 12px",
                        backgroundColor: "#e0f2fe",
                        color: "#0369a1",
                        border: "1px solid #7dd3fc",
                      }}
                    >
                      Allocated
                    </span>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ProcurementPipelineDetail;
